/* ============================================================================
   wl-stock-guard.js — a line can never hold more than the server will allow.
   ----------------------------------------------------------------------------
   Shopify's two cart endpoints disagree, which is the whole reason this exists:

     POST /cart/add.js     does NOT enforce inventory. Adding 1 to a line that is
                           already at its cap returns 200 and quantity+1.
     POST /cart/change.js  DOES enforce it, clamping to what is actually in stock.

   Measured on this store: a variant with 1 unit accepted three consecutive adds
   (1 -> 2 -> 3 -> 4, all 200), while change.js to 99 clamped it straight back to 1.
   The result was a cart line showing quantity 3 next to its own greyed-out plus
   and "Only 1 left" — two controls contradicting each other.

   Guarding each Add-to-Cart button was not enough: the product form, the quick-add
   modal, the wishlist drawer and the drawer's recommendations all add by different
   routes. They share one thing, a POST to /cart/add.js, so that is what we watch.

   THE CAP IS NEVER COMPUTED HERE. When an add lands on a line that already existed,
   we re-assert that line's quantity through change.js, and the SERVER clamps it to
   the truth. No inventory number is read, guessed or cached client-side.

   Cost: one extra request, and only on a repeat add of something already in the
   cart. A first add of a new line cannot exceed stock, so it is left alone.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__wlStockGuard) return;
  window.__wlStockGuard = true;

  function isAddUrl(url) {
    try { return /\/cart\/add(\.js)?(\?|$)/.test(String(url || '').split('#')[0]); }
    catch (e) { return false; }
  }

  /* Re-assert the quantity the add just produced. change.js enforces inventory, so if
     the add overshot, this is where it comes back down.

     COSTS NOTHING IN THE COMMON CASE. The add response already carries the line's `key`
     and resulting `quantity`, so a first add of a new line (quantity 1, which cannot
     exceed stock) does no work at all. An earlier version fetched /cart.js after EVERY
     add to find that out - measured at 454ms of pure latency on every add to cart. */
  function reconcile(line) {
    if (!line || !line.key || !(line.quantity > 1)) return;
    var asked = line.quantity;
    var vid = line.variant_id;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(line.key), quantity: asked })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (after) {
        if (!after || !after.items) return;
        writeCount(after.item_count);
        var now = 0;
        for (var j = 0; j < after.items.length; j++) {
          if (after.items[j].variant_id === vid) { now = after.items[j].quantity; break; }
        }
        if (now < asked) announce(vid, now);
      })
      .catch(function () {});
  }

  /* The shopper asked for more than exists. Say so plainly, and let the surfaces
     that render quantities repaint from the server. */
  function announce(variantId, allowed) {
    try {
      document.dispatchEvent(new CustomEvent('wl:stock-clamped', {
        bubbles: true, detail: { variantId: variantId, allowed: allowed }
      }));
    } catch (e) {}
    try {
      var api = window.wlDrawerAPI;
      if (api && api.reload) api.reload({ keepRecs: true });
    } catch (e) {}
  }

  /* /cart/add.js answers with the single line it added, or {items:[...]} for a
     multi-add. Anything else (an error payload) simply yields nothing to check. */
  function lineFrom(data) {
    if (!data || typeof data !== 'object') return null;
    if (data.items && data.items.length) return data.items[0];
    return data.variant_id ? data : null;
  }

  /* ---- instant cart-count feedback --------------------------------------
     The native add-to-cart (product page, quick-add, home/collection) re-renders the
     whole cart-drawer section before the theme fires its cart:count event — measured at
     ~1.2s. For that whole window the bottom mobile toolbar + header badge kept the OLD
     number, which reads as a wrong count and as "the add is slow". We bump every count
     badge by the quantity just added the instant /cart/add.js succeeds; the theme's own
     authoritative render still lands ~1.2s later and sets the absolute value, so this
     can only ever be briefly optimistic, never stale.

     Protection: a normal product add never touches the shipping-protection line, so
     incrementing the already-protection-adjusted displayed number stays correct — we do
     not need the protection variant id here. */
  function addedQty(body) {
    try {
      if (!body) return 1;
      if (typeof body === 'string') { var j = JSON.parse(body); return Math.max(1, parseInt(j.quantity, 10) || 1); }
      if (typeof FormData !== 'undefined' && body instanceof FormData) { return Math.max(1, parseInt(body.get('quantity'), 10) || 1); }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) { return Math.max(1, parseInt(body.get('quantity'), 10) || 1); }
    } catch (e) {}
    return 1;
  }
  function bumpCounts(delta) {
    if (!delta) return;
    try {
      document.querySelectorAll('hdt-cart-count, [data-cart-count], .hdt-cart-count, .cart-count-bubble')
        .forEach(function (el) {
          var cur = parseInt(String(el.textContent || '').replace(/[^\d-]/g, ''), 10);
          if (isNaN(cur)) cur = 0;
          el.textContent = String(cur + delta);
        });
    } catch (e) {}
  }
  function writeCount(count) {
    count = parseInt(count, 10);
    if (isNaN(count)) return;
    try {
      document.querySelectorAll('hdt-cart-count, [data-cart-count], .hdt-cart-count, .cart-count-bubble')
        .forEach(function (el) { el.textContent = String(count); });
    } catch (e) {}
  }

  /* ---- fetch ------------------------------------------------------------- */
  var _fetch = window.fetch;
  if (typeof _fetch === 'function') {
    window.fetch = function (input, init) {
      var url = (input && input.url) || input;
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var watch = method === 'POST' && isAddUrl(url);
      var reqBody = watch ? ((init && init.body) || (input && input.body)) : null;
      var p = _fetch.apply(this, arguments);
      if (!watch) return p;
      return p.then(function (res) {
        if (!res || !res.ok) return res;
        bumpCounts(addedQty(reqBody));
        try {
          res.clone().json().then(function (d) {
            reconcile(lineFrom(d));
          }).catch(function () {});
        } catch (e) {}
        return res;
      });
    };
  }

  /* ---- XMLHttpRequest ---------------------------------------------------- */
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var _open = XHR.prototype.open;
    var _send = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      this.__wlAddGuard = String(method || '').toUpperCase() === 'POST' && isAddUrl(url);
      return _open.apply(this, arguments);
    };
    XHR.prototype.send = function (data) {
      if (this.__wlAddGuard) {
        var reqBody = data;
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;
          bumpCounts(addedQty(reqBody));
          var d = null;
          try { d = typeof this.response === 'object' ? this.response : JSON.parse(this.responseText); }
          catch (e) { return; }
          reconcile(lineFrom(d));
        });
      }
      return _send.apply(this, arguments);
    };
  }
})();
